import React, { useState, useEffect, useCallback } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    IconButton,
    Collapse,
    InputAdornment,
    Tooltip,
    Divider,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Close,
    CheckCircle,
    Edit,
    Delete,
    WhatsApp,
    Sms,
    Search as SearchIcon,
    Clear as ClearIcon,
    Share as ShareIcon,
    CheckCircleOutline,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { businessAPI, invoiceAPI, orderAPI, notificationAPI } from '../api';
import ScrollStrip from './common/ScrollStrip';
import ShareButtons from './ShareButtons';
import InvoiceViewDialog from './InvoiceViewDialog';
import { buildInvoiceShareText } from '../utils/invoiceShareMessage';
import { makeRouteComparator, sortAreasByRoute } from '../utils/routeOrder';
import AreaFilterRow from './common/AreaFilterRow';

// Area filter value for "customers in no area" (area filters here key on the area name).
const NO_AREA_FILTER = '__no_area__';

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'BankTransfer', 'Cheque', 'Other'];

const ALERT_PAGE_SIZE = 50;

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const inr = (v) =>
    '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// Compact tab label: short word + a small count pill. Counts sit in the pill rather
// than the text so all three tabs fit on one line at ~390px, where the old
// "Notifications (2)" label overflowed. `alert` marks an actionable count.
const TabLabel = ({ text, count = 0, alert = false }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {text}
        {count > 0 && (
            <Box
                component="span"
                sx={{
                    px: 0.6,
                    minWidth: 18,
                    height: 18,
                    borderRadius: '9px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',  // keep 3-4 digit counts on one line
                    bgcolor: alert ? 'warning.dark' : 'action.selected',
                    color: alert ? '#fff' : 'text.secondary',
                }}
            >
                {count}
            </Box>
        )}
    </Box>
);

const DUES_TAGS = [
    { tag: 'High Dues', color: 'error' },
    { tag: 'Medium Dues', color: 'warning' },
    { tag: 'Low Dues', color: 'info' },
    { tag: 'No Dues', color: 'success' },
    { tag: 'Overpaid', color: 'success' },
];

const duesTagColor = (tag) => {
    const entry = DUES_TAGS.find(d => d.tag === tag);
    return entry ? entry.color : 'default';
};

// Local wall-clock time for a <input type="datetime-local"> value. toISOString()
// would give UTC, making recorded payment times off by the timezone offset.
const toLocalDateTimeInput = (d = new Date()) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const DuesPage = ({ businessId, business = {}, initialTab = 0, onNotificationCountChange }) => {
    const { t } = useTranslation();

    // Month navigation
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-indexed

    const [duesSummary, setDuesSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tagFilter, setTagFilter] = useState({ group: '', value: '' });
    const [customerTags, setCustomerTags] = useState([]);
    const [businessAreas, setBusinessAreas] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [duesSort, setDuesSort] = useState('dues_desc');
    // Dues rows carry area_id / sort_order / customer_name, not the customer-list field names.
    const routeComparator = React.useMemo(
        () => makeRouteComparator(businessAreas,
            { areaKey: 'area_id', orderKey: 'sort_order', nameKey: 'customer_name' }),
        [businessAreas]
    );
    const [duesVisibleCount, setDuesVisibleCount] = useState(50);
    const [summaryOpen, setSummaryOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentSort, setPaymentSort] = useState('newest');
    const [paymentAreaFilter, setPaymentAreaFilter] = useState('');
    const [paymentsVisibleCount, setPaymentsVisibleCount] = useState(50);

    // Customer payment confirmations (bill/dues claims) awaiting the vendor
    const [notifications, setNotifications] = useState([]);
    // Resolved (accepted/rejected) alerts for the selected month — paged, newest first
    const [alertHistory, setAlertHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);
    const [notifActionId, setNotifActionId] = useState(null);

    // Payment dialog (shared for record + edit)
    const [recordOpen, setRecordOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [editPaymentId, setEditPaymentId] = useState(null);
    const [paymentForm, setPaymentForm] = useState({
        amount: '',
        discount: '',
        method: 'Cash',
        notes: '',
        date: toLocalDateTimeInput(),
    });
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentSuccess, setPaymentSuccess] = useState('');

    // Delete payment confirmation
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deletePaymentId, setDeletePaymentId] = useState(null);
    const [deletePaymentCustomerId, setDeletePaymentCustomerId] = useState(null);

    // Card expansion — one open at a time, lazy-loaded
    const [expandedId, setExpandedId] = useState(null);
    const [detailCache, setDetailCache] = useState({});

    // Shared bill viewer (same server-rendered page used on the customer card & bills page)
    const [viewInvoiceId, setViewInvoiceId] = useState(null);

    // Share receipt sheet
    const [sharePayment, setSharePayment] = useState(null); // { payment_id, customer_name, customer_mobile, amount, discount, method, time, new_balance }
    const [receiptUrl, setReceiptUrl] = useState(null);
    const [receiptLoading, setReceiptLoading] = useState(false);

    const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === (now.getMonth() + 1);

    // Two years back, ending at the current month — the arrow never allowed the future either.
    const monthStrip = React.useMemo(() => {
        const fmt = new Intl.DateTimeFormat(
            ({ te: 'te-IN', hi: 'hi-IN' }[(i18next.language || 'en').slice(0, 2)] || 'en-IN'),
            { month: 'short' });
        const out = [];
        for (let i = 23; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            out.push({
                key: `${d.getFullYear()}-${d.getMonth() + 1}`,
                top: fmt.format(d),
                bottom: `'${String(d.getFullYear()).slice(-2)}`,
                accent: i === 0,          // outline the current month
                year: d.getFullYear(),
                month: d.getMonth() + 1,
            });
        }
        return out;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        try {
            const [summaryRes, areasRes, tagsRes] = await Promise.all([
                invoiceAPI.getDuesSummary(businessId, {
                    year: selectedYear,
                    month: selectedMonth,
                }),
                businessAPI.getBusinessAreas(businessId),
                businessAPI.getCustomerTags(businessId),
            ]);
            setDuesSummary(summaryRes.data);
            setBusinessAreas(areasRes.data || []);
            setCustomerTags(tagsRes.data || []);
        } catch (err) {
            console.error('Failed to load dues summary:', err);
        } finally {
            setLoading(false);
        }
    }, [businessId, selectedYear, selectedMonth]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Pending payment confirmations for bills/dues (not month-scoped).
    // Reports the count upward so the Dues nav badge stays live after accept/reject.
    const loadNotifications = useCallback(async () => {
        if (!businessId) return;
        try {
            const res = await notificationAPI.list(businessId, { status: 'pending', channel: 'dues' });
            const rows = res.data || [];
            setNotifications(rows);
            if (onNotificationCountChange) onNotificationCountChange(rows.length);
        } catch (err) {
            console.error('Failed to load payment notifications:', err);
        }
    }, [businessId, onNotificationCountChange]);

    useEffect(() => { loadNotifications(); }, [loadNotifications]);

    // Alert history for the selected month. `append=false` reloads from the top
    // (month change, or after accepting/rejecting moves a row into history).
    const loadHistory = useCallback(async (append = false) => {
        if (!businessId) return;
        setHistoryLoading(true);
        try {
            const offset = append ? alertHistory.length : 0;
            const res = await notificationAPI.list(businessId, {
                status: 'resolved', channel: 'dues',
                year: selectedYear, month: selectedMonth,
                limit: ALERT_PAGE_SIZE, offset,
            });
            const rows = res.data || [];
            setAlertHistory(prev => (append ? [...prev, ...rows] : rows));
            setHasMoreHistory(rows.length === ALERT_PAGE_SIZE);
        } catch (err) {
            console.error('Failed to load alert history:', err);
        } finally {
            setHistoryLoading(false);
        }
    }, [businessId, selectedYear, selectedMonth, alertHistory.length]);

    // Reload history from the top whenever the month changes
    useEffect(() => { loadHistory(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
        [businessId, selectedYear, selectedMonth]);

    const handleAcceptNotif = async (n) => {
        setNotifActionId(n.f_id);
        try {
            await notificationAPI.accept(businessId, n.f_id);
            // History reloads too — the row just moved from pending into it
            await Promise.all([loadNotifications(), loadData(), loadHistory(false)]);
        } catch (err) {
            console.error('Accept failed:', err);
            await loadNotifications(); // resync (e.g. already resolved)
        } finally {
            setNotifActionId(null);
        }
    };

    const handleRejectNotif = async (n) => {
        setNotifActionId(n.f_id);
        try {
            await notificationAPI.reject(businessId, n.f_id);
        } catch (err) {
            console.error('Reject failed:', err);
        } finally {
            await Promise.all([loadNotifications(), loadHistory(false)]);
            setNotifActionId(null);
        }
    };

    useEffect(() => { setDuesVisibleCount(50); }, [searchQuery, tagFilter]);
    useEffect(() => { setPaymentsVisibleCount(50); }, [paymentSearch, paymentSort]);

    // Payment handlers
    const openRecordPayment = (customer) => {
        setSelectedCustomer(customer);
        setEditPaymentId(null);
        setPaymentForm({
            amount: customer.outstanding_balance.toFixed(2),
            discount: '',
            method: 'Cash',
            notes: '',
            date: toLocalDateTimeInput(),
        });
        setPaymentError('');
        setPaymentSuccess('');
        setRecordOpen(true);
    };

    const openEditPayment = (p) => {
        setSelectedCustomer({ customer_name: p.customer_name, customer_id: p.customer_id, outstanding_balance: null });
        setEditPaymentId(p.payment_id);
        setPaymentForm({
            amount: String(p.amount),
            discount: p.discount > 0 ? String(p.discount) : '',
            method: p.payment_method,
            notes: p.notes || '',
            date: toLocalDateTimeInput(new Date(p.time)),
        });
        setPaymentError('');
        setPaymentSuccess('');
        setRecordOpen(true);
    };

    const handleSavePayment = async () => {
        if (!selectedCustomer) return;
        const amt = parseFloat(paymentForm.amount) || 0;
        const disc = parseFloat(paymentForm.discount) || 0;
        if (amt < 0 || (amt === 0 && disc <= 0)) {
            setPaymentError('Enter a payment amount or discount');
            return;
        }
        setPaymentLoading(true);
        setPaymentError('');
        try {
            if (editPaymentId) {
                await invoiceAPI.updatePayment(businessId, editPaymentId, {
                    f_amount: amt,
                    f_discount: disc,
                    f_time: new Date(paymentForm.date).toISOString(),
                    f_payment_method: paymentForm.method,
                    f_notes: paymentForm.notes || null,
                });
                setPaymentSuccess(`Payment updated for ${selectedCustomer.customer_name}`);
                setDetailCache(prev => { const n = { ...prev }; delete n[selectedCustomer.customer_id]; return n; });
                setTimeout(() => {
                    setRecordOpen(false);
                    loadData();
                }, 1200);
            } else {
                const res = await invoiceAPI.recordPayment(businessId, {
                    f_business_customer_id: selectedCustomer.customer_id,
                    f_amount: amt,
                    f_discount: disc,
                    f_time: new Date(paymentForm.date).toISOString(),
                    f_payment_method: paymentForm.method,
                    f_notes: paymentForm.notes || null,
                    f_source: 'dues',
                });
                const pd = res.data;
                setDetailCache(prev => { const n = { ...prev }; delete n[selectedCustomer.customer_id]; return n; });
                setRecordOpen(false);
                loadData();
                // Open share sheet immediately after recording
                openShareSheet({
                    payment_id: pd.payment_id,
                    customer_name: pd.customer_name,
                    customer_mobile: pd.customer_mobile || selectedCustomer.customer_mobile,
                    amount: amt,
                    discount: disc,
                    method: paymentForm.method,
                    time: new Date(paymentForm.date),
                    new_balance: pd.new_balance,
                });
            }
        } catch (err) {
            setPaymentError(err.response?.data?.detail || 'Failed to save payment');
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleDeletePayment = async () => {
        if (!deletePaymentId) return;
        try {
            await invoiceAPI.deletePayment(businessId, deletePaymentId);
            setDeleteOpen(false);
            setDeletePaymentId(null);
            if (deletePaymentCustomerId) {
                setDetailCache(prev => { const n = { ...prev }; delete n[deletePaymentCustomerId]; return n; });
                setDeletePaymentCustomerId(null);
            }
            loadData();
        } catch (err) {
            console.error('Failed to delete payment:', err);
        }
    };

    // Data derivation
    const isAgent = duesSummary?.is_agent;
    const { summary, customer_dues = [], recent_payments = [] } = duesSummary || {};

    // Build tag maps
    const duesTagMap = {};
    const areaTagMap = {};
    customerTags.forEach(tag => {
        if (tag.tag_group === 'Dues') duesTagMap[tag.f_business_customer_id] = tag.tag_name;
        if (tag.tag_group === 'Area') areaTagMap[tag.f_business_customer_id] = tag.tag_name;
    });

    // Filter by dues tags + area tags + search
    const filteredDues = customer_dues.filter(c => {
        // Tag filter
        if (tagFilter.group === 'Dues' && duesTagMap[c.customer_id] !== tagFilter.value) return false;
        if (tagFilter.group === 'Phone' && c.has_mobile !== false) return false;
        if (tagFilter.group === 'Area' && (tagFilter.value === NO_AREA_FILTER
            ? !!areaTagMap[c.customer_id]
            : areaTagMap[c.customer_id] !== tagFilter.value)) return false;
        // Search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            const nameMatch = (c.customer_name || '').toLowerCase().includes(q);
            const mobileMatch = (c.customer_mobile || '').includes(q);
            if (!nameMatch && !mobileMatch) return false;
        }
        return true;
    }).sort((a, b) => {
        // Collections usually follow the same house-to-house path as the delivery round.
        if (duesSort === 'route') return routeComparator(a, b);
        if (duesSort === 'dues_desc' || duesSort === 'dues_asc') {
            const eff = (c) => c.outstanding_balance > 0 ? c.outstanding_balance : -(c.credit_balance ?? 0);
            return duesSort === 'dues_desc' ? eff(b) - eff(a) : eff(a) - eff(b);
        }
        if (duesSort === 'last_payment_desc') {
            if (!a.last_payment_date && !b.last_payment_date) return 0;
            if (!a.last_payment_date) return 1;
            if (!b.last_payment_date) return -1;
            return new Date(b.last_payment_date) - new Date(a.last_payment_date);
        }
        if (duesSort === 'last_payment_asc') {
            if (!a.last_payment_date && !b.last_payment_date) return 0;
            if (!a.last_payment_date) return 1;
            if (!b.last_payment_date) return -1;
            return new Date(a.last_payment_date) - new Date(b.last_payment_date);
        }
        if (duesSort === 'last_order_desc') {
            if (!a.last_order_date && !b.last_order_date) return 0;
            if (!a.last_order_date) return 1;
            if (!b.last_order_date) return -1;
            return new Date(b.last_order_date) - new Date(a.last_order_date);
        }
        if (duesSort === 'last_order_asc') {
            if (!a.last_order_date && !b.last_order_date) return 0;
            if (!a.last_order_date) return 1;
            if (!b.last_order_date) return -1;
            return new Date(a.last_order_date) - new Date(b.last_order_date);
        }
        return 0;
    });

    // Filter and sort payments for Payments tab
    const filteredPayments = [...recent_payments]
        .filter(p => {
            if (paymentAreaFilter && (paymentAreaFilter === NO_AREA_FILTER
                ? !!areaTagMap[p.customer_id]
                : areaTagMap[p.customer_id] !== paymentAreaFilter)) return false;
            if (!paymentSearch) return true;
            return (p.customer_name || '').toLowerCase().includes(paymentSearch.toLowerCase());
        })
        .sort((a, b) => {
            if (paymentSort === 'newest') return new Date(b.time) - new Date(a.time);
            if (paymentSort === 'oldest') return new Date(a.time) - new Date(b.time);
            if (paymentSort === 'highest') return b.amount - a.amount;
            if (paymentSort === 'name') return (a.customer_name || '').localeCompare(b.customer_name || '');
            return 0;
        });

    const paymentsToShow = filteredPayments.slice(0, paymentsVisibleCount);

    const buildReceiptText = (sp, url) => {
        const bizName = business?.f_name || 'Your Vendor';
        const bizPhone = business?.f_phone || '';
        const lines = [
            `✅ Payment Receipt — ${bizName}`,
            ``,
            `Customer: ${sp.customer_name}`,
            `Amount Paid: ₹${Number(sp.amount).toLocaleString('en-IN')}`,
            sp.discount > 0 ? `Discount: ₹${Number(sp.discount).toLocaleString('en-IN')}` : null,
            `Method: ${sp.method}`,
            `Date: ${new Date(sp.time).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
            ``,
            sp.new_balance !== null
                ? (Number(sp.new_balance) > 0
                    ? `Remaining Balance: ₹${Number(sp.new_balance).toLocaleString('en-IN')}`
                    : `Balance: Cleared ✅`)
                : null,
            url ? `\n📄 View Receipt: ${url}` : null,
            ``,
            `Thank you!`,
            `— ${bizName}`,
            bizPhone ? `📞 ${bizPhone}` : null,
        ];
        return lines.filter(l => l !== null).join('\n');
    };

    // Open share sheet and fetch receipt PDF URL in parallel
    const openShareSheet = async (sp) => {
        setReceiptUrl(null);
        setSharePayment(sp);
        setReceiptLoading(true);
        try {
            const res = await invoiceAPI.getPaymentShareUrl(businessId, sp.payment_id);
            setReceiptUrl(res.data.share_url);
        } catch (_) {
            // URL fetch failed — share without link
        } finally {
            setReceiptLoading(false);
        }
    };

    const handleShareWhatsApp = async (sp, business_wa = false) => {
        const phone = (sp.customer_mobile || '').replace(/\D/g, '');
        const waPhone = phone.length === 10 ? `91${phone}` : phone;
        const text = buildReceiptText(sp, receiptUrl);
        const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`;
        window.location.href = url;
        try { await invoiceAPI.markPaymentShared(businessId, sp.payment_id); } catch (_) {}
        setSharePayment(null);
        setReceiptUrl(null);
        loadData();
    };

    const handleShareSms = async (sp) => {
        const phone = (sp.customer_mobile || '').replace(/\D/g, '');
        const smsPhone = phone.length === 10 ? `+91${phone}` : phone;
        const text = buildReceiptText(sp, receiptUrl);
        window.location.href = `sms:${smsPhone}?body=${encodeURIComponent(text)}`;
        try { await invoiceAPI.markPaymentShared(businessId, sp.payment_id); } catch (_) {}
        setSharePayment(null);
        setReceiptUrl(null);
        loadData();
    };

    // Share from payments tab (existing payment row)
    const openShareForPayment = (p) => {
        openShareSheet({
            payment_id: p.payment_id,
            customer_name: p.customer_name,
            customer_mobile: p.customer_mobile,
            amount: p.amount,
            discount: p.discount,
            method: p.payment_method,
            time: new Date(p.time),
            new_balance: null,
        });
    };

    // Card expansion: toggle and lazy-load invoices + payments + orders
    const handleToggleExpand = async (customerId) => {
        if (expandedId === customerId) { setExpandedId(null); return; }
        setExpandedId(customerId);
        if (detailCache[customerId]) return;
        setDetailCache(prev => ({ ...prev, [customerId]: { loading: true } }));
        // Resolve mobile from already-loaded data (listPayments API doesn't return it)
        const custMobile = (customer_dues || []).find(c => c.customer_id === customerId)?.customer_mobile
            || (recent_payments || []).find(p => p.customer_id === customerId)?.customer_mobile
            || '';
        try {
            const [invRes, pmtRes, ordRes] = await Promise.all([
                invoiceAPI.listInvoices(businessId, { customer_id: customerId, locked_only: true }),
                invoiceAPI.listPayments(businessId, { customer_id: customerId }),
                orderAPI.listOrders(businessId, { customer_id: customerId, sort_by: 'updated_at', sort_order: 'desc', limit: 20 }),
            ]);
            const rawPayments = pmtRes.data || [];
            const payments = rawPayments.map(p => ({
                payment_id: p.f_id,
                customer_id: customerId,
                customer_name: p.customer_name,
                customer_mobile: custMobile,
                amount: parseFloat(p.f_amount),
                discount: parseFloat(p.f_discount || 0),
                payment_method: p.f_payment_method,
                time: p.f_time,
                notes: p.f_notes,
                shared_at: p.f_shared_at,
            }));
            setDetailCache(prev => ({
                ...prev,
                [customerId]: { loading: false, invoices: invRes.data || [], payments, orders: ordRes.data || [] },
            }));
        } catch (_) {
            setDetailCache(prev => ({ ...prev, [customerId]: { loading: false, invoices: [], payments: [], orders: [] } }));
        }
    };

    // Share helper: build payment reminder text for a customer
    const buildReminderText = (customer) => {
        const bizName = business?.f_name || 'Your Vendor';
        const bizPhone = business?.f_phone || '';
        return [
            `Hi ${customer.customer_name},`,
            '',
            `You have an outstanding balance of ${inr(customer.outstanding_balance)} with *${bizName}*.`,
            '',
            `Please make the payment at your earliest convenience.`,
            '',
            `Thank you!`,
            `— ${bizName}`,
            bizPhone ? `📞 ${bizPhone}` : null,
        ].filter(l => l !== null).join('\n');
    };

    // Shared expanded detail panel — invoices, payments, orders
    // noPhone: the customer has no phone, so bills can't go on WhatsApp/SMS (ShareButtons greys them)
    const renderDetailPanel = (customerId, customerName, customerMobile, noPhone = false) => {
        const detail = detailCache[customerId];
        if (!detail) return null;
        if (detail.loading) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 0.5 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                </Box>
            );
        }
        const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const periodLabel = (inv) => {
            if (inv.invoice_period_display) return inv.invoice_period_display;
            return `${MONTH_SHORT[(inv.f_period_month || 1) - 1]} ${inv.f_period_year || ''}`;
        };
        return (
            <Box sx={{ pt: 1, mt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                {/* Invoices */}
                {detail.invoices.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="overline" color="text.disabled" sx={{ fontSize: '0.6rem', letterSpacing: 1 }}>Bills</Typography>
                        {detail.invoices.map(inv => {
                            const status = inv.payment_status;
                            const statusColor = status === 'Paid' ? 'success' : status === 'Partial' ? 'warning' : 'error';
                            return (
                                <Box key={inv.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {periodLabel(inv)}{inv.f_invoice_number ? ` · #${inv.f_invoice_number}` : ''}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography variant="body2" fontWeight="bold">{inr(inv.f_total_amount)}</Typography>
                                            <Chip size="small" label={status === 'Paid' ? 'Paid' : status === 'Partial' ? 'Part Paid' : 'Unpaid'} color={statusColor} sx={{ height: 16, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.5 } }} />
                                        </Box>
                                    </Box>
                                    <ShareButtons
                                        size="sm"
                                        label={false}
                                        phone={customerMobile}
                                        noPhone={noPhone}
                                        text={buildInvoiceShareText(inv, customerName)}
                                        getShareUrl={async () => {
                                            const res = await invoiceAPI.getInvoiceShareUrl(businessId, inv.f_id);
                                            return res.data?.share_url;
                                        }}
                                        onView={() => setViewInvoiceId(inv.f_id)}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Payments */}
                {detail.payments.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="overline" color="text.disabled" sx={{ fontSize: '0.6rem', letterSpacing: 1 }}>Payments</Typography>
                        {detail.payments.map(p => (
                            <Box key={p.payment_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                        {inr(p.amount)}
                                        {p.discount > 0 && (
                                            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>+{inr(p.discount)} disc.</Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(p.time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })} · {p.payment_method}
                                        {p.notes ? ` · ${p.notes}` : ''}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                    <IconButton size="small" onClick={() => openShareForPayment(p)}><ShareIcon sx={{ fontSize: 15 }} /></IconButton>
                                    <IconButton size="small" onClick={() => openEditPayment(p)}><Edit sx={{ fontSize: 15 }} /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => { setDeletePaymentId(p.payment_id); setDeletePaymentCustomerId(p.customer_id); setDeleteOpen(true); }}><Delete sx={{ fontSize: 15 }} /></IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Orders */}
                {detail.orders.length > 0 && (
                    <Box>
                        <Typography variant="overline" color="text.disabled" sx={{ fontSize: '0.6rem', letterSpacing: 1 }}>Orders</Typography>
                        {detail.orders.map(o => {
                            const oStatus = o.f_payment_status || o.payment_status || '';
                            const oColor = oStatus === 'paid' || oStatus === 'paid_directly' ? 'success' : oStatus === 'pay_later' ? 'warning' : 'default';
                            return (
                                <Box key={o.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {new Date(o.f_inserted_at || o.inserted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                            {o.f_notes ? ` · ${o.f_notes}` : ''}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography variant="body2" fontWeight="bold">{inr(o.f_total_amount || o.total_amount)}</Typography>
                                            <Chip size="small" label={oStatus} color={oColor} sx={{ height: 16, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.5 } }} />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {detail.invoices.length === 0 && detail.payments.length === 0 && detail.orders.length === 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', py: 1, fontStyle: 'italic' }}>No records found</Typography>
                )}
            </Box>
        );
    };

    // One card layout for both pending alerts (actionable) and resolved history (read-only).
    const renderAlertCard = (n, actionable) => {
        const accepted = n.f_status === 'accepted';
        const accent = actionable ? 'success.main' : (accepted ? 'success.light' : 'divider');
        return (
            <Card key={n.f_id} variant="outlined"
                sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: accent,
                      opacity: actionable ? 1 : 0.85 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={800} noWrap>{n.customer_name || 'Customer'}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {n.reference_label} · {n.f_payment_method || 'UPI'} · {new Date(n.f_inserted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                            </Typography>
                            {n.f_message && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    “{n.f_message}”
                                </Typography>
                            )}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={800}
                            color={actionable || accepted ? 'success.main' : 'text.disabled'}
                            sx={{ flexShrink: 0 }}>
                            {inr(n.f_amount)}
                        </Typography>
                    </Box>

                    {actionable ? (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                            <Button
                                fullWidth size="small" variant="contained" color="success"
                                disabled={notifActionId === n.f_id}
                                startIcon={<CheckCircle sx={{ fontSize: '1rem' }} />}
                                onClick={() => handleAcceptNotif(n)}
                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
                            >
                                Accept &amp; Record
                            </Button>
                            <Button
                                size="small" variant="outlined" color="inherit"
                                disabled={notifActionId === n.f_id}
                                onClick={() => handleRejectNotif(n)}
                                sx={{ textTransform: 'none', minHeight: 40 }}
                            >
                                Reject
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                            <Chip
                                size="small"
                                label={accepted ? 'Accepted' : 'Rejected'}
                                color={accepted ? 'success' : 'default'}
                                variant={accepted ? 'filled' : 'outlined'}
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                            />
                            {n.f_resolved_at && (
                                <Typography variant="caption" color="text.disabled">
                                    {new Date(n.f_resolved_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                                </Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            {/* ── Month Navigation — a strip of months rather than ‹ › chevrons, which say
                nothing about what they change. Ends at the current month, as the old arrow did. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                </Typography>
                {isCurrentMonth ? (
                    <Chip size="small" label="Current" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                ) : (
                    // Walking back from a far month was as many taps as getting there.
                    <Chip size="small" label="Current" color="success" variant="outlined" clickable
                        onClick={() => { setSelectedYear(now.getFullYear()); setSelectedMonth(now.getMonth() + 1); }}
                        sx={{ height: 20, fontSize: '0.7rem' }} />
                )}
            </Box>
            <Box sx={{ mb: 1.5 }}>
                <ScrollStrip
                    items={monthStrip}
                    selectedKey={`${selectedYear}-${selectedMonth}`}
                    onSelect={(_key, it) => { setSelectedYear(it.year); setSelectedMonth(it.month); }}
                />
            </Box>

            {/* ── Page tabs ── */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{
                    mb: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    minHeight: 44,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        minWidth: 0,
                        minHeight: 44,
                        px: 0.5,
                    },
                }}
            >
                <Tab label={<TabLabel text="Dues" count={customer_dues.length} />} />
                <Tab label={<TabLabel text="Payments" count={recent_payments.length} />} />
                <Tab label={<TabLabel text="Alerts" count={notifications.length} alert />} />
            </Tabs>

            {/* ── Dues Tab ── */}
            {activeTab === 0 && (<>

            {/* ── Summary ── */}
            {!loading && summary && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                    {/* Header row */}
                    <Box
                        display="flex" justifyContent="space-between" alignItems="center"
                        px={1.5} py={0.75}
                        onClick={() => setSummaryOpen(o => !o)}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Summary · {summary.customers_with_dues || 0} dues{summary.dues_cleared_count ? ` · ${summary.dues_cleared_count} cleared` : ''}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                                {inr(summary.total_outstanding)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{summaryOpen ? '▲' : '▼'}</Typography>
                        </Box>
                    </Box>
                    <Collapse in={summaryOpen}>
                        <Divider />
                        <Box px={1.5} py={1}>
                            {/* Previous Dues */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                <Typography variant="body2" color="warning.dark">
                                    Previous Dues{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({summary.previous_dues_customers || 0} customers)
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" fontWeight={500} color="warning.dark">
                                    {inr(summary.previous_dues)}
                                </Typography>
                            </Box>
                            {/* Previous dues breakdown: invoices */}
                            {(summary.previous_dues_invoices || 0) > 0 && (
                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                    <Typography variant="caption" color="text.secondary">↳ Invoice Dues</Typography>
                                    <Typography variant="caption" color="text.secondary">{inr(summary.previous_dues_invoices)}</Typography>
                                </Box>
                            )}
                            {/* Previous dues breakdown: orders */}
                            {(summary.previous_dues_orders || 0) > 0 && (
                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                    <Typography variant="caption" color="text.secondary">↳ Order Dues</Typography>
                                    <Typography variant="caption" color="text.secondary">{inr(summary.previous_dues_orders)}</Typography>
                                </Box>
                            )}
                            {/* Invoiced */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                <Typography variant="body2" color="text.primary">
                                    {(() => {
                                        const pm = selectedMonth === 1 ? 12 : selectedMonth - 1;
                                        return MONTH_NAMES[pm - 1].slice(0, 3);
                                    })()} Bills{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({summary.invoiced_count || 0} bills)
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {inr(summary.invoiced_amount)}
                                </Typography>
                            </Box>
                            {/* Orders this month */}
                            {(summary.orders_total_count || 0) > 0 && (<>
                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                    <Typography variant="body2" color="text.primary">
                                        Orders{' '}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            ({summary.orders_total_count || 0} orders · {summary.orders_total_customers || 0} customers)
                                        </Typography>
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {inr(summary.orders_total_amount)}
                                    </Typography>
                                </Box>
                                {(summary.orders_instant_count || 0) > 0 && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                        <Typography variant="caption" color="text.secondary">↳ Instant Paid ({summary.orders_instant_count})</Typography>
                                        <Typography variant="caption" color="success.main">{inr(summary.orders_instant_amount)}</Typography>
                                    </Box>
                                )}
                                {(summary.orders_invoice_count || 0) > 0 && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                        <Typography variant="caption" color="text.secondary">↳ Invoice Paid ({summary.orders_invoice_count})</Typography>
                                        <Typography variant="caption" color="text.secondary">{inr(summary.orders_invoice_amount)}</Typography>
                                    </Box>
                                )}
                                {(summary.orders_pay_later_count || 0) > 0 && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            ↳ Pay Later ({summary.orders_pay_later_count} · {summary.orders_pay_later_customers || 0} customers)
                                        </Typography>
                                        <Typography variant="caption" color="warning.dark">{inr(summary.orders_pay_later_amount)}</Typography>
                                    </Box>
                                )}
                                {(summary.orders_due_count || 0) > 0 && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.1} pl={1.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            ↳ Pending ({summary.orders_due_count})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{inr(summary.orders_due_amount)}</Typography>
                                    </Box>
                                )}
                            </>)}
                            {/* Total Collections */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                <Typography variant="body2" color="success.main">
                                    Total Collections{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({summary.payments_count || 0} payments)
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" fontWeight={500} color="success.main">
                                    {inr(summary.payments_received)}
                                </Typography>
                            </Box>
                            {/* Payment Discount */}
                            {(summary.payment_discount || 0) > 0 && (
                                <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                    <Typography variant="body2" color="warning.main">
                                        Payment Discount{' '}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            ({summary.discount_count || 0} discounts)
                                        </Typography>
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} color="warning.main">
                                        {inr(summary.payment_discount)}
                                    </Typography>
                                </Box>
                            )}
                            <Divider sx={{ my: 0.5 }} />
                            {/* Outstanding total */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                <Typography variant="body2" fontWeight="bold" color="error.main">
                                    Dues as of Today{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({summary.customers_with_dues || 0} customers)
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="error.main">
                                    {inr(summary.total_outstanding)}
                                </Typography>
                            </Box>
                            {/* Collected today */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                <Typography variant="body2" color="text.secondary">Collected Today</Typography>
                                <Typography variant="body2" fontWeight={500} color="success.main">
                                    {inr(summary.collected_today)}
                                </Typography>
                            </Box>
                        </Box>
                    </Collapse>
                </Card>
            )}

            {/* ── Search + Sort Bar ── */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                    size="small"
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setDuesVisibleCount(50); }}
                    sx={{
                        flex: '0 0 66%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: 'white',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            borderColor: searchQuery ? 'primary.main' : 'grey.200',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                            '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                    <Select
                        value={duesSort}
                        onChange={(e) => { setDuesSort(e.target.value); setDuesVisibleCount(50); }}
                        displayEmpty
                        renderValue={(val) => {
                            const labelMap = {
                                dues_desc: 'Dues ↓', dues_asc: 'Dues ↑',
                                last_payment_desc: 'Payment ↓', last_payment_asc: 'Payment ↑',
                                last_order_desc: 'Order ↓', last_order_asc: 'Order ↑',
                            };
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labelMap[val] || 'Dues ↓'}</Typography>
                                </Box>
                            );
                        }}
                        sx={{
                            borderRadius: 3,
                            bgcolor: 'white',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            borderColor: duesSort !== 'dues_desc' ? 'primary.main' : 'grey.200',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light' },
                            '& .MuiSelect-select': { py: '6px' },
                        }}
                    >
                        <MenuItem value="dues_desc">Dues ↓ (highest first)</MenuItem>
                        <MenuItem value="dues_asc">Dues ↑ (lowest first)</MenuItem>
                        <MenuItem value="route">Delivery Order</MenuItem>
                        {customer_dues.some(c => c.last_payment_date) && [
                            <MenuItem key="lp_d" value="last_payment_desc">Payment ↓ (recent first)</MenuItem>,
                            <MenuItem key="lp_a" value="last_payment_asc">Payment ↑ (oldest first)</MenuItem>,
                        ]}
                        {customer_dues.some(c => c.last_order_date) && [
                            <MenuItem key="lo_d" value="last_order_desc">Order ↓ (recent first)</MenuItem>,
                            <MenuItem key="lo_a" value="last_order_asc">Order ↑ (oldest first)</MenuItem>,
                        ]}
                    </Select>
                </FormControl>
            </Box>

            {/* ── Dues Tag Chips + Area Tags ── */}
            {!isAgent && !loading && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    <Chip
                        label={`All (${customer_dues.length})`}
                        size="small"
                        color={!tagFilter.group ? 'primary' : 'default'}
                        onClick={() => setTagFilter({ group: '', value: '' })}
                        clickable
                    />
                    {DUES_TAGS.map(({ tag, color }) => {
                        const count = customer_dues.filter(c => duesTagMap[c.customer_id] === tag).length;
                        if (count === 0) return null;
                        const isActive = tagFilter.group === 'Dues' && tagFilter.value === tag;
                        return (
                            <Chip
                                key={tag}
                                label={`${tag} (${count})`}
                                size="small"
                                color={isActive ? color : 'default'}
                                variant={isActive ? 'filled' : 'outlined'}
                                onClick={() => setTagFilter(prev =>
                                    prev.group === 'Dues' && prev.value === tag
                                        ? { group: '', value: '' }
                                        : { group: 'Dues', value: tag }
                                )}
                                clickable
                            />
                        );
                    })}
                    {/* No phone: dues reminders and bills can't go on WhatsApp — collect in person */}
                    {(() => {
                        const n = customer_dues.filter(c => c.has_mobile === false).length;
                        if (!n) return null;
                        const on = tagFilter.group === 'Phone';
                        return (
                            <Chip label={`No phone (${n})`} size="small" clickable
                                color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                onClick={() => setTagFilter(on ? { group: '', value: '' } : { group: 'Phone', value: 'none' })} />
                        );
                    })()}
                </Box>
            )}

            {/* Areas on one line — the busiest two as chips, the rest (and No area) in a list */}
            {!isAgent && !loading && (
                <AreaFilterRow
                    options={sortAreasByRoute(businessAreas)
                        .map(a => ({
                            key: a.f_name, label: a.f_name,
                            count: customer_dues.filter(c => areaTagMap[c.customer_id] === a.f_name).length,
                        }))
                        .filter(o => o.count > 0)}
                    noArea={{ key: NO_AREA_FILTER, count: customer_dues.filter(c => !areaTagMap[c.customer_id]).length }}
                    total={customer_dues.length}
                    value={tagFilter.group === 'Area' ? tagFilter.value : ''}
                    onChange={key => setTagFilter(key ? { group: 'Area', value: key } : { group: '', value: '' })}
                    sx={{ mt: -1, mb: 2 }}
                />
            )}

            {/* ── Customer Dues Cards ── */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {filteredDues.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CheckCircle sx={{ fontSize: 56, color: 'success.light', mb: 1 }} />
                            <Typography variant="body1" color="text.secondary">
                                {searchQuery ? 'No matching customers' : t('dues.noOutstanding')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {filteredDues.slice(0, duesVisibleCount).map((c) => (
                            <Card key={c.customer_id} variant="outlined" sx={{ mb: 0.75, borderColor: expandedId === c.customer_id ? 'primary.main' : 'divider' }}>
                                <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}
                                    onClick={() => handleToggleExpand(c.customer_id)} style={{ cursor: 'pointer' }}>
                                    {/* Single row: Name+tags | Balance | Actions */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Row 1: name + mobile + tag + balance */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                            {/* Left: name + mobile + tag */}
                                            <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="bold"
                                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                                                >
                                                    {c.customer_name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.4 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {c.customer_mobile}
                                                        {c.customer_address_line1 ? ` · ${c.customer_address_line1}` : ''}
                                                        {c.area_id ? ` · ${businessAreas.find(a => a.f_id === c.area_id)?.f_name || ''}` : ''}
                                                        {c.paid_this_month > 0 ? ` · Paid ${inr(c.paid_this_month)}` : ''}
                                                    </Typography>
                                                    {duesTagMap[c.customer_id] && (
                                                        <Chip
                                                            label={duesTagMap[c.customer_id].replace(' Dues', '')}
                                                            size="small"
                                                            color={duesTagColor(duesTagMap[c.customer_id])}
                                                            variant="outlined"
                                                            sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                            {/* Balance + expand chevron */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                <Typography variant="body2" fontWeight="bold"
                                                    color={c.credit_balance > 0 ? 'success.main' : 'error.main'}
                                                    sx={{ whiteSpace: 'nowrap' }}>
                                                    {c.credit_balance > 0 ? `+${inr(c.credit_balance)}` : inr(c.outstanding_balance)}
                                                </Typography>
                                                {expandedId === c.customer_id ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.disabled' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                                            </Box>
                                        </Box>
                                        {/* Row 2: Actions */}
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <ShareButtons
                                                    phone={c.customer_mobile || ''}
                                                    noPhone={c.has_mobile === false}
                                                    text={buildReminderText(c)}
                                                    size="sm"
                                                    label={false}
                                                />
                                            </Box>
                                            <Tooltip title="Record Payment">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={(e) => { e.stopPropagation(); openRecordPayment(c); }}
                                                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                                                >
                                                    Record Payment
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                        {/* Expanded detail */}
                                        <Collapse in={expandedId === c.customer_id} unmountOnExit>
                                            {renderDetailPanel(c.customer_id, c.customer_name, c.customer_mobile, c.has_mobile === false)}
                                        </Collapse>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                            {filteredDues.length > duesVisibleCount && (
                                <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                                    <Button variant="outlined" size="small" onClick={() => setDuesVisibleCount(v => v + 50)}>
                                        Load more ({filteredDues.length - duesVisibleCount} remaining)
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}

                </>
            )}

            </>)}

            {/* ── Payments Tab ── */}
            {activeTab === 1 && (<>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Summary line */}
                        {summary && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Showing {paymentsToShow.length} of {recent_payments.length} payment{recent_payments.length !== 1 ? 's' : ''} this month
                                </Typography>
                                <Typography variant="caption" fontWeight="bold" color="success.main">
                                    {inr(summary.payments_received)} collected
                                </Typography>
                            </Box>
                        )}

                        {/* Search + Sort */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <TextField
                                size="small"
                                placeholder="Search customer..."
                                value={paymentSearch}
                                onChange={(e) => setPaymentSearch(e.target.value)}
                                sx={{
                                    flex: '0 0 66%',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        border: '1.5px solid',
                                        borderColor: paymentSearch ? 'primary.main' : 'grey.200',
                                        transition: 'border-color 0.2s, box-shadow 0.2s',
                                        '& fieldset': { border: 'none' },
                                        '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                                        '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: paymentSearch ? 'primary.main' : 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: paymentSearch ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setPaymentSearch('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <FormControl size="small" sx={{ flexShrink: 0, width: 'calc(34% - 8px)' }}>
                                <Select
                                    value={paymentSort}
                                    onChange={(e) => setPaymentSort(e.target.value)}
                                    displayEmpty
                                    renderValue={(val) => {
                                        const labelMap = { newest: 'Newest ↓', oldest: 'Oldest ↑', highest: 'Amount ↓', name: 'Name' };
                                        return (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{labelMap[val] || 'Newest ↓'}</Typography>
                                            </Box>
                                        );
                                    }}
                                    sx={{
                                        borderRadius: 3,
                                        bgcolor: 'white',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        border: '1.5px solid',
                                        borderColor: paymentSort !== 'newest' ? 'primary.main' : 'grey.200',
                                        '& fieldset': { border: 'none' },
                                        '&:hover': { borderColor: 'primary.light' },
                                        '& .MuiSelect-select': { py: '6px' },
                                    }}
                                >
                                    <MenuItem value="newest">Newest first</MenuItem>
                                    <MenuItem value="oldest">Oldest first</MenuItem>
                                    <MenuItem value="highest">Highest amount</MenuItem>
                                    <MenuItem value="name">By name</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <AreaFilterRow
                            options={sortAreasByRoute(businessAreas)
                                .map(a => ({
                                    key: a.f_name, label: a.f_name,
                                    count: recent_payments.filter(p => areaTagMap[p.customer_id] === a.f_name).length,
                                }))
                                .filter(o => o.count > 0)}
                            noArea={{ key: NO_AREA_FILTER, count: recent_payments.filter(p => !areaTagMap[p.customer_id]).length }}
                            total={recent_payments.length}
                            value={paymentAreaFilter}
                            onChange={key => { setPaymentAreaFilter(key); setPaymentsVisibleCount(50); }}
                            sx={{ mb: 2 }}
                        />

                        {/* Payment cards */}
                        {filteredPayments.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography variant="body1" color="text.secondary">
                                    {paymentSearch ? 'No matching payments' : 'No payments this month'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {paymentsToShow.map((p) => (
                                    <Card key={p.payment_id} variant="outlined" sx={{ mb: 0.75, borderColor: expandedId === p.customer_id ? 'primary.main' : 'divider' }}>
                                        <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}
                                            onClick={() => handleToggleExpand(p.customer_id)} style={{ cursor: 'pointer' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight="bold" noWrap>{p.customer_name}</Typography>
                                                    {(p.customer_address_line1 || p.customer_area_id) && (
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                            {[p.customer_address_line1, businessAreas.find(a => a.f_id === p.customer_area_id)?.f_name].filter(Boolean).join(' · ')}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {p.payment_method} · {new Date(p.time).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                        {p.recorded_by ? ` · ${p.recorded_by}` : ''}
                                                    </Typography>
                                                    {p.notes && (
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontStyle: 'italic' }}>
                                                            {p.notes}
                                                        </Typography>
                                                    )}
                                                    {p.shared_at && (
                                                        <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                            <CheckCircleOutline sx={{ fontSize: 12 }} />
                                                            Sent {new Date(p.shared_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                                        +{inr(p.amount)}
                                                    </Typography>
                                                    {p.discount > 0 && (
                                                        <Typography variant="caption" color="warning.main" sx={{ lineHeight: 1 }}>
                                                            +{inr(p.discount)} disc.
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                    <IconButton size="small" onClick={() => openShareForPayment(p)}>
                                                        <ShareIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => openEditPayment(p)}>
                                                        <Edit sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => { setDeletePaymentId(p.payment_id); setDeletePaymentCustomerId(p.customer_id); setDeleteOpen(true); }}>
                                                        <Delete sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    {expandedId === p.customer_id ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.disabled' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                                                </Box>
                                            </Box>
                                            <Collapse in={expandedId === p.customer_id} unmountOnExit>
                                                {renderDetailPanel(p.customer_id, p.customer_name, p.customer_mobile, !p.customer_mobile)}
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                ))}
                                {filteredPayments.length > paymentsVisibleCount && (
                                    <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                                        <Button variant="outlined" size="small" onClick={() => setPaymentsVisibleCount(v => v + 50)}>
                                            Load more ({filteredPayments.length - paymentsVisibleCount} remaining)
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </>)}

            {/* ── Notifications Tab (customer payment confirmations for bills/dues) ── */}
            {activeTab === 2 && (
                <Box>
                    {/* Needs action */}
                    {notifications.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, px: 2, lineHeight: 1.6 }}>
                            No new payment updates.<br />
                            When a customer taps “I've paid” on a bill, it shows here for you to accept.
                        </Typography>
                    ) : notifications.map(n => renderAlertCard(n, true))}

                    {/* Earlier alerts for the selected month — accepted/rejected, newest first */}
                    {(alertHistory.length > 0 || historyLoading) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                Earlier in {MONTH_NAMES[selectedMonth - 1]}
                            </Typography>
                            {alertHistory.map(n => renderAlertCard(n, false))}
                            {historyLoading && (
                                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                            )}
                            {hasMoreHistory && !historyLoading && (
                                <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                                    <Button variant="outlined" size="small" onClick={() => loadHistory(true)}
                                        sx={{ textTransform: 'none' }}>
                                        Load more
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}

                    {notifications.length === 0 && alertHistory.length === 0 && !historyLoading && (
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', pb: 4 }}>
                            No earlier alerts in {MONTH_NAMES[selectedMonth - 1]}.
                        </Typography>
                    )}
                </Box>
            )}

            {/* ── Record / Edit Payment Dialog ── */}
            <Dialog
                open={recordOpen}
                onClose={() => !paymentLoading && setRecordOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {editPaymentId ? 'Edit Payment' : t('dues.recordPayment')}
                    <IconButton size="small" onClick={() => setRecordOpen(false)} disabled={paymentLoading}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {paymentError && <Alert severity="error" sx={{ mb: 2 }}>{paymentError}</Alert>}
                    {paymentSuccess && <Alert severity="success" sx={{ mb: 2 }}>{paymentSuccess}</Alert>}
                    {selectedCustomer && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography fontWeight="bold">{selectedCustomer.customer_name}</Typography>
                            {!editPaymentId && selectedCustomer.outstanding_balance != null && (
                                <Typography variant="body2" color="error.main">
                                    {t('dues.outstandingBalance')} {inr(selectedCustomer.outstanding_balance)}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        label="Amount Paid (₹)"
                        type="number"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Discount / Adjustment (₹)"
                        type="number"
                        value={paymentForm.discount}
                        onChange={(e) => setPaymentForm(f => ({ ...f, discount: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                        helperText="Optional — amount forgiven that reduces the balance"
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t('dues.paymentMethod')}</InputLabel>
                        <Select
                            value={paymentForm.method}
                            label={t('dues.paymentMethod')}
                            onChange={(e) => setPaymentForm(f => ({ ...f, method: e.target.value }))}
                        >
                            {PAYMENT_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label={t('dues.dateTime')}
                        type="datetime-local"
                        value={paymentForm.date}
                        onChange={(e) => setPaymentForm(f => ({ ...f, date: e.target.value }))}
                        sx={{ mb: 2 }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label={t('dues.notes')}
                        multiline
                        rows={2}
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRecordOpen(false)} disabled={paymentLoading}>
                        {t('dues.cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSavePayment} disabled={paymentLoading}>
                        {paymentLoading ? <CircularProgress size={20} /> : editPaymentId ? 'Save Changes' : t('dues.savePayment')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Payment Confirmation ── */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
                <DialogTitle>Delete Payment?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will remove the payment record. The customer's balance will be recalculated.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeletePayment}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Shared bill viewer — same server-rendered page that gets shared */}
            <InvoiceViewDialog businessId={businessId} invoiceId={viewInvoiceId} onClose={() => setViewInvoiceId(null)} />

            {/* ── Share Receipt Sheet ── */}
            <Dialog
                open={!!sharePayment}
                onClose={() => { setSharePayment(null); setReceiptUrl(null); }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ShareIcon fontSize="small" />
                        Share Receipt
                    </Box>
                    <IconButton size="small" onClick={() => { setSharePayment(null); setReceiptUrl(null); }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {sharePayment && (
                        <>
                            {/* Receipt preview */}
                            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                                <Typography variant="body2" fontWeight="bold">{sharePayment.customer_name}</Typography>
                                <Typography variant="body2">
                                    Paid {inr(sharePayment.amount)}
                                    {sharePayment.discount > 0 ? ` + ${inr(sharePayment.discount)} disc.` : ''}
                                    {' · '}{sharePayment.method}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(sharePayment.time).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                {sharePayment.new_balance !== null && (
                                    <Typography variant="caption" display="block" color={Number(sharePayment.new_balance) <= 0 ? 'success.main' : 'warning.main'} fontWeight="bold">
                                        {Number(sharePayment.new_balance) <= 0 ? 'Balance Cleared ✅' : `Remaining: ${inr(sharePayment.new_balance)}`}
                                    </Typography>
                                )}
                            </Box>
                            {/* Receipt PDF status */}
                            {receiptLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <CircularProgress size={14} />
                                    <Typography variant="caption" color="text.secondary">Preparing receipt PDF…</Typography>
                                </Box>
                            )}
                            {receiptUrl && !receiptLoading && (
                                <Chip
                                    size="small"
                                    icon={<CheckCircleOutline />}
                                    label="Receipt PDF ready"
                                    color="success"
                                    sx={{ mb: 1.5 }}
                                />
                            )}
                            {/* Share buttons */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <WhatsApp />}
                                    disabled={receiptLoading}
                                    onClick={() => handleShareWhatsApp(sharePayment, false)}
                                    sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe5d' }, textTransform: 'none' }}
                                >
                                    WhatsApp
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <WhatsApp />}
                                    disabled={receiptLoading}
                                    onClick={() => handleShareWhatsApp(sharePayment, true)}
                                    sx={{ bgcolor: '#128C7E', '&:hover': { bgcolor: '#075E54' }, textTransform: 'none' }}
                                >
                                    WhatsApp Business
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={receiptLoading ? <CircularProgress size={16} color="inherit" /> : <Sms />}
                                    disabled={receiptLoading}
                                    onClick={() => handleShareSms(sharePayment)}
                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, textTransform: 'none' }}
                                >
                                    SMS
                                </Button>
                                <Button
                                    fullWidth
                                    variant="text"
                                    color="inherit"
                                    onClick={() => { setSharePayment(null); setReceiptUrl(null); }}
                                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                                >
                                    Skip
                                </Button>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default DuesPage;

